import { useCallback, useContext, useMemo } from 'react';
import styled from '@emotion/styled';
import { createId } from '@paralleldrive/cuid2';
import type { Chat } from '@trinity/chat';
import { ChatActionType, ChatHintType } from '@trinity/chat';
import { addMonths } from 'date-fns';
import { ChatResponsePane } from '@trinity/chat-components/components/Chat/ResponsePane';
import { ChatStateDispatchContext } from '@trinity/chat-components/components/Chat.State/context';
import {
  MessageQueryHintContainer,
  MessageQueryHint,
} from '@trinity/chat-components/components/presentation/Message.QueryHint';
import { useCookie } from '@trinity/chat-components/hooks/useCookie';
import { ActiveChatSessionContext } from './context';
import { useChatTextInputControls } from './hooks/useChatTextInputControls';
import { useSubmitQueryMutation } from './hooks/useSubmitQueryMutation';

type Props = {
  className?: string;
  hints: Chat.QueryHint.Item[];
  queryIdentifier?: string;
};

type RelatedQuestionsProps = Props & {
  profileKey: string;
};

export const MessageQueryHints = ({ className, hints, queryIdentifier: parentQueryIdentifier }: Props) => {
  const { focusChatInputTextArea } = useChatTextInputControls();
  const dispatch = useContext(ChatStateDispatchContext);

  const submitQueryMutation = useSubmitQueryMutation();
  const session = useContext(ActiveChatSessionContext);

  const handleHint = useCallback(
    (hint: Chat.QueryHint.Item, index: number) => () => {
      if (hint.type === ChatHintType.Text || hint.type === ChatHintType.Prompt) {
        dispatch({
          type: 'input-changed',
          payload: {
            value: hint.displayValue,
            promptHintIdentifier:
              hint.type === ChatHintType.Prompt ? hint.metadata?.promptIdentifier : undefined,
          },
        });
        focusChatInputTextArea();
      }

      if (hint.type === ChatHintType.AnalysisTags) {
        const queryIdentifier = createId();

        submitQueryMutation.mutate({
          identifier: queryIdentifier,
          parentQueryIdentifier: parentQueryIdentifier || null,
          query: hint.displayValue,
          context: session.context,
          action: {
            type: ChatActionType.StartSegmentAnalysis,
          },
        });
      }

      if (hint.type === ChatHintType.FindQuotes) {
        const queryIdentifier = createId();

        submitQueryMutation.mutate({
          identifier: queryIdentifier,
          parentQueryIdentifier: parentQueryIdentifier || null,
          query: hint.displayValue,
          action: null,
          context: session.context,
        });
      }
    },
    [dispatch, focusChatInputTextArea, submitQueryMutation, session.context, parentQueryIdentifier]
  );

  return (
    <MessageQueryHintContainer className={className}>
      {hints.map((hint, i) => (
        <MessageQueryHint key={`${i}`} hint={hint} onClick={handleHint(hint, i)} disabled={hint.disabled} />
      ))}
    </MessageQueryHintContainer>
  );
};

export const RelatedQuestionsPane = (props: RelatedQuestionsProps) => {
  const [open, toggleOpen] = useProfileKeyCookie(props.profileKey);

  return (
    <ChatResponsePane
      className={props.className}
      open={open}
      toggleOpen={toggleOpen}
      title="Related Questions"
    >
      <PaneHints hints={props.hints} queryIdentifier={props.queryIdentifier} />
    </ChatResponsePane>
  );
};

const useProfileKeyCookie = (key: string) => {
  const cookieKey = `hcp-profile-related-questions-${key}`;
  const [value, setCookieInternal] = useCookie(cookieKey);

  const open = useMemo(() => {
    return value === 'open' || value === undefined;
  }, [value]);

  const toggle = useCallback(() => {
    setCookieInternal({
      expires: addMonths(new Date(), 36),
      value: open ? 'closed' : 'open',
    });
  }, [open, setCookieInternal]);

  return [open, toggle] as const;
};

const PaneHints = styled(MessageQueryHints)({
  minHeight: 0,
  marginTop: 10,
  transition: `height 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms`,
});
